import {
  DocumentPropertyValue,
  PersonPropertyValue,
  PropertyValue as PropertyValueType,
} from '../../../api/clients/PropertyClient'
import { useCallback, useLayoutEffect, useRef, useState, RefObject, MutableRefObject } from 'react'
import Styles from '../PropertyValue.module.scss'
import SingleSelect from './SingleSelect'

const useArrangeMultiSelectProperties = (
  ref: RefObject<HTMLDivElement>,
  listRef: MutableRefObject<(HTMLDivElement | null)[]>,
  inline: boolean,
  selectedPropertyValues: PropertyValueType[] | DocumentPropertyValue[] | PersonPropertyValue[],
) => {
  const selectedPropertiesDep = JSON.stringify(selectedPropertyValues.map((i) => i.id))
  const [counter, setCounter] = useState(0)

  const hideProperties = useCallback(() => {
    const width = ref.current?.offsetWidth ?? 150 // Default width for a property cell in table/right rail

    setCounter(0)

    let total = 30 // Account for if we need to render the counter
    let counter = 0

    listRef.current.forEach((el, index) => {
      if (!el) return
      total += el?.offsetWidth
      if (index !== listRef.current.length - 1) total += 5 // Account for the 5px margin between each item

      if (total > width) {
        if (index > 0) {
          el.style.display = 'none'
          counter++
        }
      } else {
        el.style.display = 'inline-block'
      }
    })

    if (counter > 0) {
      setCounter(counter)
    }
  }, [ref, listRef])

  useLayoutEffect(() => {
    if (!inline) {
      return
    }

    hideProperties()
  }, [hideProperties, selectedPropertiesDep, inline])

  return counter
}

function MultiSelect({
  inline = false,
  selectedPropertyValues,
  smallPill = false,
}: {
  inline?: boolean
  selectedPropertyValues: PropertyValueType[] | DocumentPropertyValue[] | PersonPropertyValue[]
  smallPill?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<Array<HTMLDivElement | null>>([])

  const hiddenCount = useArrangeMultiSelectProperties(ref, listRef, inline, selectedPropertyValues)

  if (inline) {
    return (
      <div ref={ref} className={Styles.multiSelectInline}>
        <div className={Styles.multiSelectInlineContent}>
          {selectedPropertyValues.map((pv, index) => (
            <SingleSelect
              key={`${pv.id}-${index}`}
              ref={(el: HTMLDivElement) => {
                listRef.current[index] = el
              }}
              propertyValue={pv}
              smallPill={smallPill}
            />
          ))}
          {hiddenCount > 0 && <span>+{hiddenCount}</span>}
        </div>
      </div>
    )
  }

  return (
    <>
      {selectedPropertyValues.map((pvm, index) => (
        <SingleSelect key={index} propertyValue={pvm} smallPill={smallPill} />
      ))}
    </>
  )
}

export default MultiSelect
